/* eslint-disable custom-eslint-rules/force-logicalOperandsMustBeBoolean */
import { ReactNode, RefObject } from "react";
import { useTheme } from "styled-components";
import { Flex } from "../../Flex/Flex.js";
import { AddLinear } from "../../Iconsax/AddLinear.js";
import { CloseLinear } from "../../Iconsax/CloseLinear.js";
import { ICONSAX_SIZE } from "../../Iconsax/Iconsax.types.js";
import { List } from "../../List/List.js";
import { PopoverItem } from "../../Popover/components/PopoverItem/PopoverItem.js";
import { Spinner } from "../../Spinner/Spinner.js";
import { TagV2 } from "../../TagV2/TagV2.js";
import { Text } from "../../Text/Text.js";
import { TextSize, TextWeight } from "../../Text/Text.types.js";
import { rem } from "../../Theme/Theme.constants.js";
import { useTranslationsContext } from "../../TranslationsProvider/TranslationsProvider.context.js";
import { TranslationsProviderHookProps } from "../../TranslationsProvider/TranslationsProvider.types.js";
import { LoaderWrapper, PopoverContainer } from "../SelectV2.style.js";
import { SelectV2Option, SelectV2Value } from "../SelectV2.types.js";

type OptionsMenuProps<Value extends SelectV2Value> = {
    options: SelectV2Option<Value>[];
    filteredOptions: SelectV2Option<Value>[];
    value: Value | Value[] | undefined;
    multiple: boolean;
    maxMenuHeight: string;
    focusedIndex: number;
    isLoading?: boolean;
    loader?: ReactNode;
    hasMore?: boolean;
    loadMore?: () => void;
    onOptionClick: (value: Value) => void;
    onClearAll?: () => void;
    search?: string;
    creatable?: boolean;
    isCreating?: boolean;
    onClickCreate?: (value: string, closeMenu: () => void) => void;
    closeMenu: () => void;
    customTranslations?: Partial<TranslationsProviderHookProps["translations"]["select"]>;
    hideCreateLabel?: boolean;
    highlightedIndex?: number;
    hideSelectedOptions?: boolean;
    clearable?: boolean;
    popoverRef?: RefObject<HTMLDivElement>;
};

export const OptionsMenu = <Value extends SelectV2Value>({
    options,
    filteredOptions,
    value,
    multiple,
    maxMenuHeight,
    focusedIndex,
    isLoading,
    loader,
    hasMore,
    loadMore,
    onOptionClick,
    onClearAll,
    search,
    creatable,
    isCreating,
    onClickCreate,
    closeMenu,
    customTranslations,
    hideCreateLabel,
    highlightedIndex,
    hideSelectedOptions,
    clearable,
    popoverRef,
}: OptionsMenuProps<Value>) => {
    const theme = useTheme();
    const { translations } = useTranslationsContext();

    // Generate custom loader or use default spinner
    const loaderElement = (
        <LoaderWrapper>
            {loader ?? (
                <Flex justify="center" style={{ width: "100%" }}>
                    <Spinner />
                </Flex>
            )}
        </LoaderWrapper>
    );

    return (
        <PopoverContainer maxMenuHeight={maxMenuHeight}>
            {/* Selected values section for multiple select */}
            {multiple && (value as Value[]).length > 0 && (!hideSelectedOptions || clearable) && (
                <Flex
                    direction="column"
                    style={{
                        padding: theme.spacing[16],
                        borderBottom: `2px solid ${theme.grayscale[50]}`,
                    }}
                    gap="8"
                >
                    <Flex width="100%" align="center" justify={hideSelectedOptions ? "end" : "between"} ref={popoverRef}>
                        {!hideSelectedOptions && (
                            <Text size={TextSize["XX-SMALL"]} color={theme.grayscale[70]}>
                                {customTranslations?.selected || translations.select?.selected || "Selected"}:
                            </Text>
                        )}

                        {clearable && (
                            <Text size={TextSize["XX-SMALL"]} weight={TextWeight.BOLD} color={theme.colors.error[100]} onClick={onClearAll}>
                                {customTranslations?.clear || translations.select?.clear || "Clear"}
                            </Text>
                        )}
                    </Flex>

                    {!hideSelectedOptions && (
                        <Flex gap="4" wrap="wrap" width="100%">
                            {(value as Value[]).map((v) => {
                                const option = options.find((o) => o.value === v);
                                return option ? (
                                    <TagV2
                                        key={`${option.label}${option.value}`}
                                        iconRight={CloseLinear}
                                        onIconClick={(e) => {
                                            e.stopPropagation();
                                            onOptionClick(v);
                                        }}
                                    >
                                        {option.label}
                                    </TagV2>
                                ) : null;
                            })}
                        </Flex>
                    )}
                </Flex>
            )}

            {/* Loading state when no options */}
            {isLoading && filteredOptions.length === 0 && loaderElement}

            {/* Create option item */}
            {creatable && !isLoading && search && !options.find((op) => op.label.toLocaleLowerCase() === search.trim().toLocaleLowerCase()) && (
                <PopoverItem onClick={() => onClickCreate?.(search, closeMenu)}>
                    <Flex gap="8" align="center" justify="center" style={{ height: rem(2) }} ref={popoverRef}>
                        {!hideCreateLabel && <AddLinear size={ICONSAX_SIZE.SMALL} color={theme.colors.primary[100]} />}

                        {!isCreating ? (
                            <Text color={theme.colors.primary[100]} weight={TextWeight.BOLD} size={TextSize["X-SMALL"]}>
                                {!hideCreateLabel && (customTranslations?.create || translations.select?.create || "Create: ")}
                                <Text color={theme.colors.primary[100]} weight={TextWeight.SEMIBOLD} size={TextSize["X-SMALL"]}>
                                    {` '${search.trim()}'`}
                                </Text>
                            </Text>
                        ) : (
                            <Spinner color="primary" />
                        )}
                    </Flex>
                </PopoverItem>
            )}

            {/* Options list */}
            <List
                key={`options-list-${filteredOptions.length}-${search}`}
                totalCount={hasMore ? filteredOptions.length + 1 : filteredOptions.length}
                data={filteredOptions.map((o, index) => ({
                    ...o,
                    _id: o.value.toString(),
                    isSelected: multiple ? Array.isArray(value) && value.includes(o.value) : o.value === value,
                    isFocused: index === focusedIndex,
                    onClick: () => onOptionClick(o.value),
                }))}
                overflowY="auto"
                loadMore={loadMore}
                renderItem={(option, _, index) => {
                    return option.render ? (
                        option.render({
                            isSelected: option.isSelected,
                            isFocused: option.isFocused,
                            isHighlighted: highlightedIndex === index,
                            onClick: option.onClick,
                            label: option.label,
                            value: option.value,
                        })
                    ) : (
                        <PopoverItem
                            key={option.value}
                            isSelected={option.isSelected || option.isFocused}
                            onClick={option.onClick}
                            isHighlighted={highlightedIndex === index}
                        >
                            {option.label}
                        </PopoverItem>
                    );
                }}
                itemSpacing="0"
                emptyList={
                    (!creatable || !search) && filteredOptions.length === 0 ? (
                        <PopoverItem>
                            <Text size={TextSize["XX-SMALL"]} color={theme.grayscale[80]}>
                                {customTranslations?.noOptions || translations.select?.noOptions || "No options founded"}
                            </Text>
                        </PopoverItem>
                    ) : null
                }
                loader={loaderElement}
            />
        </PopoverContainer>
    );
};
